import { SHIPPING_ADDRESS_FIELDS, type ShippingAddress } from "@loot-os/api-contract";
import {
  LEGACY_ADDRESS_FIELD_KEYS,
  LEGACY_ADDRESS_NAME_KEY,
} from "@loot-os/fulfillment-core/order/legacy-address";

/** An address form's fields as typed, every one a string ("" = empty). */
export type ShippingAddressDraft = Record<keyof ShippingAddress, string>;

/**
 * A form's starting draft from a stored address. Reads only string fields, because an order's
 * address is jsonb a tenant wrote and may predate the schema; a field the address lacks is read
 * from its legacy key when it has one, `name` split into first and last. `country` is passed in,
 * since a stored one may be a code or a display name.
 */
export function toShippingAddressDraft(
  address: unknown,
  country?: string | null,
): ShippingAddressDraft {
  const fields =
    typeof address === "object" && address !== null && !Array.isArray(address)
      ? (address as Record<string, unknown>)
      : {};
  const text = (key: string | undefined) =>
    key !== undefined && typeof fields[key] === "string" ? (fields[key] as string) : "";
  const draft = {} as ShippingAddressDraft;
  for (const field of SHIPPING_ADDRESS_FIELDS) {
    draft[field.key] = text(field.key) || text(legacyKey(field.key));
  }
  if (!draft.firstName && !draft.lastName) {
    const [first = "", ...rest] = text(LEGACY_ADDRESS_NAME_KEY).trim().split(/\s+/);
    draft.firstName = first;
    draft.lastName = rest.join(" ");
  }
  if (country !== undefined) draft.country = country ?? "";
  return draft;
}

/** The legacy key a form field is read from when the address lacks the field itself. */
function legacyKey(field: keyof ShippingAddress): string | undefined {
  return (LEGACY_ADDRESS_FIELD_KEYS as Partial<Record<keyof ShippingAddress, string>>)[field];
}

/** The draft without its blank fields, so an untouched optional field is omitted, not stored as "". */
export function withoutBlanks(draft: ShippingAddressDraft): Partial<ShippingAddressDraft> {
  const out: Partial<ShippingAddressDraft> = {};
  for (const field of SHIPPING_ADDRESS_FIELDS) {
    const value = draft[field.key].trim();
    if (value) out[field.key] = value;
  }
  return out;
}

/** What the form says for each field-rule refusal (`shippingAddressFieldIssue`). */
export const ADDRESS_ISSUE_MESSAGES = {
  invalid_region: "Pick the state or territory.",
  postal_code_mismatch: "The postal code does not fit the country.",
  unknown_country: "Enter a country name or its two-letter code.",
} as const;
